import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, GitMerge, X } from "lucide-react";
import { CombinePersonDialog } from "@/components/people/CombinePersonDialog";

const SIG = { name: "Same name", email: "Same email", phone: "Same phone" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function GroupCard({ g, label, onCombine, onDismiss }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`dup-group-${g.key}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${g.confidence === "high" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{g.confidence === "high" ? "Likely" : "Possible"}</span>
        {g.signals.map((s) => <span key={s.signal + s.value} className="text-xs text-slate-500">{SIG[s.signal]}: <span className="font-medium text-slate-700">{s.value}</span></span>)}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {g.people.map((p) => (
          <div key={p.id} className="rounded-md bg-slate-50 px-3 py-2 text-sm">
            <Link to={`/people/detail/${p.id}`} className="font-medium text-[#006090] hover:underline" data-testid={`dup-person-${p.human_id}`}>{p.first_name} {p.last_name}</Link>
            <span className="text-xs text-slate-400"> {p.human_id}</span>
            <p className="text-xs text-slate-500">{p.email || "no email"} · {p.phone || "no phone"} · added {fmt(p.created_at)}</p>
            <p className="text-xs text-slate-500">{p.classifications.map(label).join(", ") || "No classification"}</p>
          </div>))}
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" onClick={() => onCombine(g)} className="bg-[#006090] hover:bg-[#004E75]" data-testid={`dup-combine-${g.key}`}><GitMerge className="w-4 h-4 mr-1" />Combine</Button>
        <Button size="sm" variant="outline" onClick={() => onDismiss(g)} data-testid={`dup-dismiss-${g.key}`}><X className="w-4 h-4 mr-1" />Not the same person</Button>
      </div>
    </div>
  );
}

export default function DuplicateFinder() {
  const [districts, setDistricts] = useState([]);
  const [org, setOrg] = useState(localStorage.getItem("tr_district") || "");
  const [groups, setGroups] = useState(null);
  const [classTypes, setClassTypes] = useState([]);
  const [combine, setCombine] = useState(null);
  useEffect(() => {
    api.get("/agency-districts").then((r) => setDistricts(r.data.items || [])).catch(() => {});
    api.get("/classification-types").then((r) => setClassTypes(r.data.items || [])).catch(() => {});
  }, []);
  const load = useCallback(() => {
    if (!org) { setGroups(null); return; }
    api.get("/records/persons/duplicate-finder", { params: { organization_id: org } }).then((r) => setGroups(r.data.groups)).catch((e) => toast.error(apiError(e)));
  }, [org]);
  useEffect(() => { load(); }, [load]);
  const label = (k) => (Array.isArray(classTypes) ? classTypes.find((c) => c.key === k)?.label : null) || k;
  const dismiss = async (g) => {
    if (!window.confirm("Mark these records as different people? They won't be suggested again.")) return;
    try { await api.post("/records/persons/duplicate-finder/dismiss", { person_ids: g.people.map((p) => p.id) }); toast.success("Marked as different people"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [first, second] = combine?.people || [];
  return (
    <div className="space-y-5 max-w-5xl" data-testid="duplicate-finder-page">
      <div><h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-[#006090]" />Duplicate Finder</h1>
        <p className="text-sm text-slate-500 mt-1">People in one district who share a name, email or phone. Review each pair, then combine or mark them as different people. Nothing changes until you confirm.</p></div>
      <label className="block max-w-sm text-xs font-semibold text-slate-600">District<select value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-300 px-2" data-testid="dup-district">
        <option value="">Choose…</option>{districts.map((d) => <option key={d.org_id} value={d.org_id}>{d.name}</option>)}</select></label>
      {groups && <p className="text-sm text-slate-700" data-testid="dup-count">{groups.length ? `${groups.length} possible duplicate ${groups.length === 1 ? "group" : "groups"}` : "No likely duplicates found in this district"}</p>}
      <div className="space-y-3">{(groups || []).map((g) => <GroupCard key={g.key} g={g} label={label} onCombine={setCombine} onDismiss={dismiss} />)}</div>
      {combine && <CombinePersonDialog person={first} initialOther={second} classTypes={Array.isArray(classTypes) ? classTypes : []}
        onClose={() => setCombine(null)} onDone={() => { setCombine(null); load(); }} />}
    </div>
  );
}
